// Typ: FindInradiusFromSidesTask
// Temat: Okręgi w trójkącie → Okrąg wpisany w trójkąt (trzy boki)

import { InlineMath, BlockMath } from 'react-katex';
import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview
} from '../../../../components/inputs/RootFractionInput';

// ===== utils =====
const rnd = (a, b) => Math.floor(Math.random() * (b - a + 1)) + a;

function gcd(a, b) {
  a = Math.abs(a); b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

// n = outside^2 * inside (inside square-free)
function simplifySqrtInt(n) {
  if (n <= 0) return { outside: 0, inside: 1 };
  let out = 1, inside = n;
  for (let i = 2; i * i <= inside; i++) {
    const sq = i * i;
    while (inside % sq === 0) {
      out *= i;
      inside /= sq;
    }
  }
  return { outside: out, inside };
}

function formatCoeffWithRoot(coeff, den, root) {
  const d = gcd(Math.abs(coeff), Math.abs(den));
  let num = coeff / d;
  let denS = den / d;

  if (!root || root === 1) {
    const sign = num < 0 ? '-' : '';
    const absNum = Math.abs(num);
    if (denS === 1) return `${num}`;
    if (absNum < denS) return `${sign}\\dfrac{${absNum}}{${denS}}`;
    const whole = Math.floor(absNum / denS);
    const rem = absNum % denS;
    if (rem === 0) return `${sign}${whole}`;
    return `${sign}${whole}\\dfrac{${rem}}{${denS}}`;
  }

  const sign = num < 0 ? '-' : '';
  const nabs = Math.abs(num);
  if (nabs === 1 && denS === 1) return `${sign}\\sqrt{${root}}`;
  if (denS === 1) return `${sign}${nabs}\\sqrt{${root}}`;
  if (nabs === 1) return `${sign}\\dfrac{\\sqrt{${root}}}{${denS}}`;
  return `${sign}\\dfrac{${nabs}\\sqrt{${root}}}{${denS}}`;
}

// ===== generator =====
export function generateTask() {
  // boki spełniają nierówności trójkąta
  let a, b, c;
  do {
    a = rnd(6, 24);
    b = rnd(6, 24);
    c = rnd(6, 24);
  } while (!(a + b > c && a + c > b && b + c > a));

  return {
    type: 'FindInradiusFromSidesTask',
    data: { a, b, c }
  };
}

// ===== render task =====
export function renderTask({ data }) {
  const { a, b, c } = data;
  return (
    <div style={{ marginTop: 16, marginBottom: 8 }}>
      W trójkącie <InlineMath math={'ABC'} /> dane są boki
      <InlineMath math={`\\;a=${a}`} />, <InlineMath math={`\\;b=${b}`} /> i <InlineMath math={`\\;c=${c}`} />.{' '}
      Oblicz promień okręgu wpisanego{' '}
      <span style={{ whiteSpace: 'nowrap' }}>
        <InlineMath math={'r'} />.
      </span>
    </div>
  );
}


// ===== input =====
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>
      <RootFractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label={'r'}
        placeholder='np. (5√6)/7, √3/2, 1.25'
      />
    </div>
  );
}

// ===== check =====
export function checkAnswer({ data }, userAnswer) {
  const { a, b, c } = data;
  // r = sqrt((a+b+c)(-a+b+c)(a-b+c)(a+b-c)) / (2(a+b+c))
  const t1 = a + b + c;
  const t2 = -a + b + c;
  const t3 = a - b + c;
  const t4 = a + b - c;
  const prod = t1 * t2 * t3 * t4;
  const expected = Math.sqrt(prod) / (2 * (a + b + c));

  const raw = typeof userAnswer === 'object' ? userAnswer.raw : userAnswer;
  let parsed =
    typeof userAnswer === 'object' && userAnswer.parsed != null
      ? userAnswer.parsed
      : parseRootFractionInput(raw);

  if ((parsed == null || !Number.isFinite(parsed)) && typeof raw === 'string') {
    const asNumber = Number(raw.replace(',', '.'));
    if (Number.isFinite(asNumber)) parsed = asNumber;
  }

  if (parsed == null || !Number.isFinite(parsed)) return false;
  return Math.abs(parsed - expected) <= 0.01; // akceptuj dziesiętne ±0.01
}

// ===== answer =====
export function renderAnswer({ data }) {
  const { a, b, c } = data;
  const t1 = a + b + c;
  const t2 = -a + b + c;
  const t3 = a - b + c;
  const t4 = a + b - c;
  const prod = t1 * t2 * t3 * t4;

  const { outside, inside } = simplifySqrtInt(prod);
  const denom = 2 * (a + b + c);
  const latex = formatCoeffWithRoot(outside, denom, inside);

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`r = ${latex}`} />
    </div>
  );
}

// ===== explanation =====
export function renderExplanation({ data }) {
  const { a, b, c } = data;

  // półobwód i różnice
  const p = (a + b + c) / 2;
  const pa = p - a, pb = p - b, pc = p - c;

  // pełny Heron: P = sqrt(p(p-a)(p-b)(p-c)) = sqrt(prod)/4
  const prod = (a + b + c) * (-a + b + c) * (a - b + c) * (a + b - c);
  const { outside, inside } = simplifySqrtInt(prod);

  // ładny zapis P po uproszczeniu (jak w zadaniu o polu)
  const P_simplified = formatCoeffWithRoot(outside, 4, inside);

  // finalny zapis r: r = (outside*sqrt(inside)) / (2(a+b+c)), z automatycznym skróceniem
  const r_final = formatCoeffWithRoot(outside, 2 * (a + b + c), inside);

  return (
    <div style={{ textAlign: 'left', lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> Wzór Herona:</div>
      <BlockMath math={'P = \\sqrt{p\\,(p-a)\\,(p-b)\\,(p-c)},\\quad p=\\dfrac{a+b+c}{2}'} />

      <div><strong>Krok 2.</strong> Liczymy półobwód i różnice:</div>
      <BlockMath math={`p = \\dfrac{${a}+${b}+${c}}{2} = ${p}`} />
      <BlockMath math={`p-a = ${p}-${a} = ${pa}`} />
      <BlockMath math={`p-b = ${p}-${b} = ${pb}`} />
      <BlockMath math={`p-c = ${p}-${c} = ${pc}`} />

      <div><strong>Krok 3.</strong> Podstawiamy do wzoru i upraszczamy (jak w zadaniu o polu):</div>
      <BlockMath math={`P = \\sqrt{${p}\\cdot ${pa}\\cdot ${pb}\\cdot ${pc}}`} />
      <BlockMath math={`P = \\dfrac{\\sqrt{${prod}}}{4}`} />
      <BlockMath math={`P = ${P_simplified}`} />

      <div><strong>Krok 4.</strong> Promień okręgu wpisanego:</div>
      <BlockMath math={'r = \\dfrac{P}{p}'} />
      <BlockMath math={`r = \\dfrac{${P_simplified}}{${p}}`} />
      <BlockMath math={`r = ${r_final}`} />
    </div>
  );
}




// ===== user answer =====
export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === 'object' ? userAnswer.raw : userAnswer;
  if (!raw || typeof raw !== 'string' || raw.trim() === '') {
    return <i>(brak odpowiedzi)</i>;
  }
  return (
    <div style={{ marginTop: 8 }}>
      <InlineMath math={formatLatexPreview(raw, 'r')} />
    </div>
  );
}

// ===== default export (dla agregatora) =====
export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer
};
